import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const AddMobileAppStore = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addMobileAppStore(values);

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Mobile app store added successfully",
        });
        form.resetFields();
        navigate("/mobile-app-store");
      } else {
        notify.error({
          message: response.data.message || "Failed to add mobile app store",
        });
      }
    } catch (error) {
      console.error("Error adding mobile app store:", error);
      notify.error({
        message: error?.data?.message || "Error adding mobile app store",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/mobile-app-store");
  };

  useEffect(() => {
    setPageTitle("Add Mobile App Store");
  }, [setPageTitle]);

  return (
    <div className="min-h-screen">
      <Card
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            is_active: "Y",
            is_maintenance: "N",
            is_force_update: "N",
          }}
          className="space-y-6"
        >
          {/* Mobile App Store Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <AppstoreOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Mobile App Store Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Name
                    </span>
                  }
                  name="store_name"
                  rules={[
                    { required: true, message: "Please enter store name" },
                  ]}
                >
                  <Input
                    placeholder="Enter store name"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Key
                    </span>
                  }
                  name="store_key"
                  rules={[
                    { required: true, message: "Please enter key value" },
                  ]}
                >
                  <Input placeholder="Enter key value" className="rounded-lg" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Flexible Version
                    </span>
                  }
                  name="flexible_version"
                >
                  <Input
                    placeholder="Enter flexible version"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Flexible Build Number
                    </span>
                  }
                  name="flexible_build_number"
                >
                  <Input
                    placeholder="Enter flexible build number"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Immediate Version
                    </span>
                  }
                  name="immediate_version"
                >
                  <Input
                    placeholder="Enter immediate version"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Immediate Build Number
                    </span>
                  }
                  name="immediate_build_number"
                >
                  <Input
                    placeholder="Enter immediate build number"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Maintenance Mode
                    </span>
                  }
                  name="is_maintenance"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="warning" text="Yes" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="No" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Force Update
                    </span>
                  }
                  name="is_force_update"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="warning" text="Yes" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="No" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Mobile App Store"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddMobileAppStore;
